/**
 * Company logos sit on the dark glass avatar. A dark mark on a transparent background
 * (The New York Times' black "T" favicon, Apple, GitHub…) disappears there, so such a
 * logo gets a light plate behind it. Decided from the loaded image's own pixels.
 */

/** RGBA pixels → true when the logo is a dark mark with see-through areas. */
export function needsLightPlate(rgba: Uint8ClampedArray | number[]): boolean {
  let seeThrough = 0;
  let solid = 0;
  let lumSum = 0;
  const n = Math.floor(rgba.length / 4);
  for (let i = 0; i < n; i++) {
    const a = rgba[i * 4 + 3] ?? 0;
    if (a < 128) {
      seeThrough++;
      continue;
    }
    solid++;
    lumSum += (0.2126 * (rgba[i * 4] ?? 0) + 0.7152 * (rgba[i * 4 + 1] ?? 0) + 0.0722 * (rgba[i * 4 + 2] ?? 0)) / 255;
  }
  if (!n || !solid) return false;
  // mostly opaque logos bring their own background
  if (seeThrough / n < 0.2) return false;
  return lumSum / solid < 0.35;
}

/** Same check on a loaded <img> (same-origin badge URL, so the canvas is readable). */
export function imgNeedsLightPlate(img: HTMLImageElement): boolean {
  try {
    const size = 24;
    const c = document.createElement('canvas');
    c.width = size;
    c.height = size;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    if (!ctx) return false;
    ctx.drawImage(img, 0, 0, size, size);
    return needsLightPlate(ctx.getImageData(0, 0, size, size).data);
  } catch {
    return false;
  }
}
